import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../api/hooks';
import type { BookingPolicyResponse, UpdateBookingPolicyRequest } from '../api/types';
import { AsyncSection } from '../components/AsyncSection';
import { PageHeader } from '../components/PageHeader';
import { ErrorState } from '../components/states/StateViews';
import { useAuthStore } from '../auth/authStore';

/**
 * The clinic's booking rules (FR-303, DEC-028).
 *
 * Four numbers, saved together. They are read together when a booking is judged, so a partial save
 * would leave a policy nobody could predict — and each one is explained in the terms a clinic
 * thinks in ("how late can someone still book?") rather than in the terms the API stores them in.
 *
 * Nothing enforces them yet; the scheduling engine arrives at M3. The screen says so rather than
 * implying the rules are already in force.
 */
/**
 * The four numbers, all present.
 *
 * The generated request type makes every field optional because the schema does; the form never
 * sends a partial policy, so it works in a shape that says so and lets the compiler enforce it.
 */
interface PolicyDraft {
  readonly minLeadMinutes: number;
  readonly bookingHorizonDays: number;
  readonly freeCancellationHours: number;
  readonly freeRescheduleHours: number;
}

export function PoliciesPage() {
  const { t } = useTranslation();
  const clinicId = useAuthStore((state) => state.activeClinicId);
  const canManage = useAuthStore((state) => state.permissions).has('POLICY_MANAGE');
  const [draft, setDraft] = useState<PolicyDraft | null>(null);

  const policies = useApiQuery<BookingPolicyResponse>(
    queryKeys.policies(clinicId ?? ''),
    `/clinics/${clinicId}/settings/policies`,
    { enabled: Boolean(clinicId) },
  );

  const save = useApiMutation<BookingPolicyResponse, UpdateBookingPolicyRequest>(
    (body) => ({ path: `/clinics/${clinicId}/settings/policies`, options: { method: 'PUT', body } }),
    { invalidates: [queryKeys.policies(clinicId ?? '')], onSuccess: () => setDraft(null) },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.policies')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader title={t('nav.items.policies')} description={t('policies.description')} />

      <AsyncSection query={policies}>
        {(saved) => {
          const current: PolicyDraft = draft ?? {
            minLeadMinutes: saved.minLeadMinutes ?? 120,
            bookingHorizonDays: saved.bookingHorizonDays ?? 60,
            freeCancellationHours: saved.freeCancellationHours ?? 24,
            freeRescheduleHours: saved.freeRescheduleHours ?? 24,
          };
          const update = (change: Partial<PolicyDraft>) => setDraft({ ...current, ...change });

          return (
            <form
              className="panel"
              onSubmit={(event) => {
                event.preventDefault();
                save.mutate(current);
              }}
            >
              <div className="form-grid">
                <NumberField
                  label={t('policies.minLead')}
                  hint={t('policies.minLeadHint')}
                  unit={t('policies.minutes')}
                  value={current.minLeadMinutes}
                  min={0}
                  disabled={!canManage}
                  onChange={(minLeadMinutes) => update({ minLeadMinutes })}
                />
                <NumberField
                  label={t('policies.horizon')}
                  hint={t('policies.horizonHint')}
                  unit={t('policies.days')}
                  value={current.bookingHorizonDays}
                  min={1}
                  disabled={!canManage}
                  onChange={(bookingHorizonDays) => update({ bookingHorizonDays })}
                />
                <NumberField
                  label={t('policies.freeCancellation')}
                  hint={t('policies.freeCancellationHint')}
                  unit={t('policies.hours')}
                  value={current.freeCancellationHours}
                  min={0}
                  disabled={!canManage}
                  onChange={(freeCancellationHours) => update({ freeCancellationHours })}
                />
                <NumberField
                  label={t('policies.freeReschedule')}
                  hint={t('policies.freeRescheduleHint')}
                  unit={t('policies.hours')}
                  value={current.freeRescheduleHours}
                  min={0}
                  disabled={!canManage}
                  onChange={(freeRescheduleHours) => update({ freeRescheduleHours })}
                />
              </div>

              <p className="field__hint" style={{ marginBlockStart: 'var(--space-4)' }}>
                {t('policies.enforcementHint')}
              </p>

              {save.error ? <ErrorState error={save.error} /> : null}

              {canManage ? (
                <div className="toolbar" style={{ marginBlockStart: 'var(--space-4)' }}>
                  <button
                    type="submit"
                    className="button button--primary"
                    disabled={draft === null || save.isPending}
                  >
                    {save.isPending ? t('catalog.saving') : t('policies.save')}
                  </button>
                  <button
                    type="button"
                    className="button button--ghost"
                    disabled={draft === null}
                    onClick={() => setDraft(null)}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              ) : null}
            </form>
          );
        }}
      </AsyncSection>
    </>
  );
}

function NumberField({
  label,
  hint,
  unit,
  value,
  min,
  disabled,
  onChange,
}: {
  readonly label: string;
  readonly hint: string;
  readonly unit: string;
  readonly value: number;
  readonly min: number;
  readonly disabled: boolean;
  readonly onChange: (value: number) => void;
}) {
  return (
    <label className="field">
      <span className="field__label">
        {label} <span className="field__hint">({unit})</span>
      </span>
      <input
        type="number"
        className="field__input numeric"
        min={min}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <span className="field__hint">{hint}</span>
    </label>
  );
}
